import { useEffect, useState } from "react";
import { Table, Button, Popconfirm, notification, Dropdown, Menu } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../hooks/useAuth";
import {
  deleteChangeOrder,
  fetchChangeOrders,
} from "../../services/contractsAPI";
import NewOrUpdateChangeOrder from "./NewOrUpdateChangeOrder";
import { formatDate } from "../../utils/dateUtils";
import { getChangeOrderLabel, isTimeChangeOrdersEnabled, formatDurationDays } from "../../utils/projectUtils";

const ChangeOrdersTable = ({
  contractId,
  isViewModeContract,
  isVisibleNewOrUpdateChangeOrder,
  setIsVisibleNewOrUpdateChangeOrder,
}) => {
  const { selectedProject } = useAuth();
  const projectId = selectedProject.project_id;
  const userRole = selectedProject.role;
  const isOwnerOrManager =
    userRole === "owner" || userRole === "project_manager";
  const coLabel = getChangeOrderLabel(selectedProject);
  const coLabelPlural = getChangeOrderLabel(selectedProject, true);
  const timeChangeOrdersEnabled = isTimeChangeOrdersEnabled(selectedProject);
  const queryClient = useQueryClient();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [selectedChangeOrder, setSelectedChangeOrder] = useState(null);
  const [isViewMode, setIsViewMode] = useState(false);
  const {
    data: changeOrders,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [
      "changeOrders",
      projectId,
      pagination.current,
      pagination.pageSize,
      contractId,
    ],
    queryFn: () =>
      fetchChangeOrders(
        contractId,
        projectId,
        pagination.current,
        pagination.pageSize
      ),
    enabled: !!contractId && !!projectId,
  });

  const deleteMutation = useMutation({
    mutationFn: ({ contractId, changeOrderId, projectId }) =>
      deleteChangeOrder({ contractId, changeOrderId, projectId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contracts"] });
      queryClient.invalidateQueries(["change-orders", projectId]);
      queryClient.invalidateQueries(["changeOrders", projectId]);
      notification.success({ message: `${coLabel} deleted successfully` });
    },
    onError: (error) => {
      notification.error({
        message: `Failed to delete ${coLabel}`,
        description: error.message,
      });
    },
  });

  useEffect(() => {
    if (!isVisibleNewOrUpdateChangeOrder) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisibleNewOrUpdateChangeOrder]);

  const handleUpdateClick = (changeOrder) => {
    setSelectedChangeOrder(changeOrder);
    setIsViewMode(false);
    setIsVisibleNewOrUpdateChangeOrder(true);
  };

  const handleViewClick = (changeOrder) => {
    setSelectedChangeOrder(changeOrder);
    setIsViewMode(true);
    setIsVisibleNewOrUpdateChangeOrder(true);
  };

  const handleDeleteClick = (changeOrderId) => {
    deleteMutation.mutate({
      contractId,
      changeOrderId,
      projectId,
    });
  };

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value || 0);
  };


  const columns = [
    {
      title: "Number",
      dataIndex: "number",
      key: "number",
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
    },
    {
      title: "Invoiced Date",
      dataIndex: "invoiced_date",
      key: "invoiced_date",
      render: (text) => {
        const formattedDate = formatDate(text);
        return formattedDate;
      },
    },
    {
      title: "Paid Date",
      dataIndex: "paid_date",
      key: "paid_date",
      render: (text) => {
        const formattedDate = formatDate(text);
        return formattedDate;
      },
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amount) => formatCurrency(amount),
    },
    ...(timeChangeOrdersEnabled
      ? [
          {
            title: "Days",
            dataIndex: "days",
            key: "days",
            render: (days) => formatDurationDays(days),
          },
        ]
      : []),
    isOwnerOrManager && {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item key="1" onClick={() => handleViewClick(record)}>
                <Button className="w-full">View</Button>
              </Menu.Item>
              {!isViewModeContract && (
                <>
                  <Menu.Item key="2" onClick={() => handleUpdateClick(record)}>
                    <Button className="w-full">Update</Button>
                  </Menu.Item>
                  <Menu.Item key="3">
                    <Popconfirm
                      title={`Are you sure you want to delete this ${coLabel.toLowerCase()}?`}
                      onConfirm={() => handleDeleteClick(record?._id)}
                      okText="Yes"
                      cancelText="No"
                    >
                      <Button className="w-full" danger>
                        Delete
                      </Button>
                    </Popconfirm>
                  </Menu.Item>
                </>
              )}
            </Menu>
          }
          trigger={["click"]}
        >
          <Button icon={<EditOutlined />} size="medium"></Button>
        </Dropdown>
      ),
    },
  ].filter(Boolean);

  return (
    <div>
      <h3 className="text-xl font-bold mb-4">{coLabelPlural}</h3>
      <div className="flex gap-4 mb-4 justify-end">
        {isOwnerOrManager && !isViewModeContract && (
          <Button
            type="primary"
            onClick={() => {
              setIsViewMode(false);
              setSelectedChangeOrder(null);
              setIsVisibleNewOrUpdateChangeOrder(true);
            }}
          >
            New
          </Button>
        )}
      </div>
      <Table
        columns={columns}
        dataSource={changeOrders?.change_orders}
        rowKey="id"
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: changeOrders?.total,
          showSizeChanger: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,
        }}
        onChange={handleTableChange}
        loading={isLoading}
      />
      {isVisibleNewOrUpdateChangeOrder && (
        <NewOrUpdateChangeOrder
          visible={isVisibleNewOrUpdateChangeOrder}
          onClose={() => {
            setIsVisibleNewOrUpdateChangeOrder(false);
          }}
          changeOrderData={selectedChangeOrder}
          isViewMode={isViewMode}
          contractId={contractId}
        />
      )}
    </div>
  );
};

export default ChangeOrdersTable;
